import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import Notification from '@this/src/notification';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import { isDebugPanelEnabled } from '../debug/isDebugPanelEnabled';
import { useDebugLogContextOptional } from '../debug';
import type { ChatMessage, FeedbackType } from '../types/messages';
import type { UserInfo } from '../types/user';
import type { Blocker } from '../types/conversationState';
import { getTripStatusLabel, TRIP_STATUS, TRIP_STATUS_LABELS } from '../types/tripStatus';
import type { ProposalBundle } from '../types/proposalBundles';
import type { ReservationInfo } from '../api/reservationInfo';
import type { ArrangementPaymentParams } from '../api/arrangement';
import MessageSection from './message_section/MessageSection';
import type { FormValues } from '../types/reservationInfoForm';
import {
  EMPTY_CARD_INPUT,
  isCardInputComplete,
  joinCardNumber,
  NEW_CARD_UID,
  PAYMENT_GATEWAYS,
  PAYMENT_METHODS,
  resolveSelectedCardUid,
  type PaymentFormState
} from '../types/payment';
import { useGmoTokenization } from '../hooks/useGmoTokenization';
import { useAiMessages, type ProposalNotification } from '../hooks/useAiMessages';
import { useCalendarConnection } from '../hooks/useCalendarConnection';
import { getCurrentAiModeLabel } from '../utils/getCurrentAiModeLabel';
import HamburgerButton from '@this/shared/header/hamburger_button';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AiModeBadge from './AiModeBadge';
import CalendarConnectionBadge from './CalendarConnectionBadge';

interface Props {
  tripId: string;
  trip: TripResponseArgs | null;
  user?: UserInfo;
  pusherKey: string;
  pusherChannelPrefix: string;
  headerTitle?: string;
  subtitle?: string;
  children?: React.ReactNode;
  enableMessaging?: boolean;
  initialMessages?: ChatMessage[];
  onProposalReceived?: (notification: ProposalNotification) => void;
  onReservationFormReceived: () => void;
  proposalBundles?: ProposalBundle[];
  onSelectItem?: (bundleId: number, itemId: number) => void;
  onCloseItem?: (bundleId: number) => void;
  onBundleFeedbackUpdate?: (bundleId: number, feedback: FeedbackType, commentSubmitted: boolean) => void;
  reservationInfo?: ReservationInfo | null;
  onSubmitReservationInfo?: (values: FormValues) => Promise<boolean>;
  isSubmittingReservationInfo?: boolean;
  onArrangementRequestSuccess?: () => Promise<void>;
  onReservationInfoFormEditableChange: (editable: boolean) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  appendToInput: (template: string) => void;
  resetInput: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  blockers: Blocker[];
  searchTargets: string[];
  onConversationStateChanged: () => void;
  onArrangementRequestedChange: (value: boolean) => void;
  isSpLayout?: boolean;
  onOpenSidebarDrawer?: () => void;
  onOpenInfoDrawer?: () => void;
  proposalCount?: number;
}

const ChatArea = ({
  tripId,
  trip,
  user,
  pusherKey,
  pusherChannelPrefix,
  headerTitle,
  subtitle,
  children,
  enableMessaging = true,
  initialMessages = [],
  onProposalReceived,
  onReservationFormReceived,
  proposalBundles = [],
  onSelectItem,
  onCloseItem,
  onBundleFeedbackUpdate,
  reservationInfo,
  onSubmitReservationInfo,
  isSubmittingReservationInfo,
  onArrangementRequestSuccess,
  onReservationInfoFormEditableChange,
  inputValue,
  onInputChange,
  appendToInput,
  resetInput,
  textareaRef,
  blockers,
  searchTargets,
  onConversationStateChanged,
  onArrangementRequestedChange,
  isSpLayout = false,
  onOpenSidebarDrawer,
  onOpenInfoDrawer,
  proposalCount = 0
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const debugContext = useDebugLogContextOptional();
  const { isCalendarConnected } = useCalendarConnection();

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  }, []);
  const {
    messages,
    sending,
    sendText,
    disableReservationInfoForm,
    handleArrangementRequest,
    isRequestingArrangement,
    isArrangementRequested,
    inputDisabled,
    proposalChangeDisabled,
    isReservationInfoFormEditable
  } = useAiMessages({
    tripId,
    pusherKey,
    pusherChannelPrefix,
    enabled: enableMessaging,
    initialMessages,
    onProposalReceived,
    onReservationFormReceived,
    onScrollToBottom: scrollToBottom,
    onConversationStateChanged
  });

  const [paymentFormValue, setPaymentFormValue] = useState<PaymentFormState>({
    paymentMethod: PAYMENT_METHODS.BILLING,
    selectedCardUid: null,
    cardInput: EMPTY_CARD_INPUT
  });

  const paymentInfo = reservationInfo?.payment ?? null;
  const isGmoNewCard =
    paymentFormValue.paymentMethod === PAYMENT_METHODS.CARD &&
    paymentInfo?.gatewayType === PAYMENT_GATEWAYS.GMO &&
    paymentFormValue.selectedCardUid === NEW_CARD_UID;

  const {
    ready: gmoReady,
    loadError: gmoLoadError,
    tokenize
  } = useGmoTokenization({
    shopId: paymentInfo?.gmoShopId ?? '',
    tokenHost: paymentInfo?.gmoTokenHost ?? '',
    enabled: isGmoNewCard
  });

  const resolvedSelectedCardUid = resolveSelectedCardUid(paymentInfo, paymentFormValue);

  const isPaymentInvalid =
    (paymentInfo != null &&
      paymentFormValue.paymentMethod === PAYMENT_METHODS.CARD &&
      resolvedSelectedCardUid == null) ||
    (isGmoNewCard && (!gmoReady || gmoLoadError || !isCardInputComplete(paymentFormValue.cardInput)));

  useEffect(() => {
    onReservationInfoFormEditableChange(isReservationInfoFormEditable);
  }, [isReservationInfoFormEditable, onReservationInfoFormEditableChange]);

  useEffect(() => {
    onArrangementRequestedChange(isArrangementRequested);
  }, [isArrangementRequested, onArrangementRequestedChange]);

  const canSend = useMemo(
    () => enableMessaging && !inputDisabled && inputValue.trim().length > 0 && !sending,
    [enableMessaging, inputDisabled, inputValue, sending]
  );

  const handleSend = useCallback(async () => {
    if (!enableMessaging) return;
    const text = inputValue;
    resetInput();
    await sendText(text);
  }, [enableMessaging, inputValue, resetInput, sendText]);

  const handleArrangementRequestWithRefetch = useCallback(async () => {
    let payment: ArrangementPaymentParams = {};
    if (paymentInfo != null && paymentFormValue.paymentMethod === PAYMENT_METHODS.CARD) {
      if (isGmoNewCard) {
        const { cardInput } = paymentFormValue;
        const expire = `${cardInput.expireYear}${cardInput.expireMonth}`;
        let token: string;
        try {
          token = await tokenize({
            cardno: joinCardNumber(cardInput),
            expire,
            securitycode: cardInput.securityCode,
            holdername: cardInput.holderName
          });
        } catch (error) {
          const message =
            error instanceof Error && error.message
              ? error.message
              : 'カード情報の処理に失敗しました。入力内容をご確認ください。';
          Notification.error(message);
          return;
        }
        payment = {
          payment_method: PAYMENT_METHODS.CARD,
          payment_gateway_type: paymentInfo.gatewayType,
          token,
          payment_transaction: { uid: NEW_CARD_UID }
        };
      } else if (resolvedSelectedCardUid && resolvedSelectedCardUid !== NEW_CARD_UID) {
        payment = {
          payment_method: PAYMENT_METHODS.CARD,
          payment_gateway_type: paymentInfo.gatewayType,
          payment_transaction: { uid: resolvedSelectedCardUid }
        };
      }
    }

    const success = await handleArrangementRequest(payment);
    if (success && onArrangementRequestSuccess) {
      await onArrangementRequestSuccess();
    }
  }, [
    handleArrangementRequest,
    onArrangementRequestSuccess,
    paymentInfo,
    paymentFormValue,
    resolvedSelectedCardUid,
    isGmoNewCard,
    tokenize
  ]);

  const handleSubmitReservationInfo = useCallback(
    async (values: FormValues) => {
      if (!onSubmitReservationInfo) return;
      const success = await onSubmitReservationInfo(values);
      if (success) {
        disableReservationInfoForm();
      }
    },
    [onSubmitReservationInfo, disableReservationInfoForm]
  );

  const initialBundlesLoadedRef = useRef(false);
  const prevTripIdRef = useRef(tripId);
  useEffect(() => {
    if (prevTripIdRef.current !== tripId) {
      initialBundlesLoadedRef.current = false;
      prevTripIdRef.current = tripId;
    }
  }, [tripId]);

  // NOTE: proposalBundlesがない会話ではスクロール不要（会話量が少ないため）
  // tripId変更時の古いproposalBundlesによる誤発火は、親コンポーネントでの非同期更新順序により実際には発生しない
  useEffect(() => {
    if (proposalBundles.length > 0 && !initialBundlesLoadedRef.current) {
      initialBundlesLoadedRef.current = true;
      scrollToBottom();
    }
  }, [proposalBundles, scrollToBottom]);

  const tripStatusLabel = useMemo(() => {
    if (isArrangementRequested) {
      return TRIP_STATUS_LABELS[TRIP_STATUS.ARRANGING];
    }
    return getTripStatusLabel(trip?.status, trip?.draft);
  }, [isArrangementRequested, trip?.status, trip?.draft]);

  const currentAiModeLabel = useMemo(() => getCurrentAiModeLabel(messages), [messages]);

  const headerBadgeLabel = useMemo(() => {
    if (isArrangementRequested) {
      return tripStatusLabel;
    }
    if (trip?.draft === true) {
      return currentAiModeLabel;
    }
    return tripStatusLabel;
  }, [currentAiModeLabel, isArrangementRequested, trip?.draft, tripStatusLabel]);

  const headerSubtitle = useMemo(() => {
    if (isArrangementRequested) {
      return subtitle || tripStatusLabel;
    }
    if (trip?.draft === true) {
      return currentAiModeLabel;
    }
    return subtitle || tripStatusLabel;
  }, [currentAiModeLabel, isArrangementRequested, subtitle, trip?.draft, tripStatusLabel]);

  const handleCalendarDateSelect = useCallback(
    (dateText: string) => {
      const [, monthText, dayText] = dateText.split('-');
      const suggestionText = `${Number(monthText)}/${Number(
        dayText
      )}を出発日として、この条件で出張提案を進めてください`;

      onInputChange(suggestionText);

      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onInputChange]
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          {isSpLayout && onOpenSidebarDrawer ? (
            <div style={styles.hamburgerWrap}>
              <HamburgerButton active={false} onClick={onOpenSidebarDrawer} />
            </div>
          ) : null}
          <div>
            <div style={styles.title}>{headerTitle || `旅程番号：${tripId}`}</div>
            <div style={styles.subtitle}>{headerSubtitle}</div>
          </div>
        </div>
        <div style={styles.headerButtons}>
          {isDebugPanelEnabled() && debugContext && (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => debugContext.setIsOpen(!debugContext.isOpen)}
            >
              エラーテスト: {debugContext.isOpen ? 'ON' : 'OFF'}
            </button>
          )}
          {isCalendarConnected && <CalendarConnectionBadge />}
          <AiModeBadge label={headerBadgeLabel} />
          {isSpLayout && onOpenInfoDrawer ? (
            <button type="button" style={styles.infoButton} onClick={onOpenInfoDrawer} aria-label="提案情報を開く">
              <InfoOutlinedIcon style={{ fontSize: 22 }} htmlColor={theme.linkColor} />
              {proposalCount > 0 ? <span style={styles.infoBadge}>{proposalCount}</span> : null}
            </button>
          ) : null}
        </div>
      </div>
      {trip ? (
        <MessageSection
          trip={trip}
          user={user}
          messages={messages}
          isSpLayout={isSpLayout}
          proposalBundles={proposalBundles}
          onSelectItem={onSelectItem}
          onCloseItem={onCloseItem}
          onBundleFeedbackUpdate={onBundleFeedbackUpdate}
          reservationInfo={reservationInfo}
          onSubmitReservationInfo={handleSubmitReservationInfo}
          isSubmittingReservationInfo={isSubmittingReservationInfo}
          onArrangementRequest={handleArrangementRequestWithRefetch}
          isRequestingArrangement={isRequestingArrangement}
          isArrangementRequested={isArrangementRequested}
          isPaymentInvalid={isPaymentInvalid}
          proposalChangeDisabled={proposalChangeDisabled}
          scrollContainerRef={scrollContainerRef}
          onCalendarDateSelect={handleCalendarDateSelect}
          paymentFormValue={paymentFormValue}
          onPaymentFormChange={setPaymentFormValue}
          chipHint={{
            blockers,
            searchTargets,
            onChipClick: appendToInput
          }}
        />
      ) : (
        <div style={styles.loadingContainer}>読み込み中...</div>
      )}
      {children || (
        <div style={{ ...styles.inputArea, ...(isSpLayout ? styles.inputAreaSp : {}) }}>
          <textarea
            placeholder="メッセージを入力して下さい"
            ref={textareaRef}
            style={{
              ...styles.textarea,
              ...(isSpLayout ? styles.textareaSp : {}),
              ...(inputDisabled ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {})
            }}
            rows={2}
            value={inputValue}
            onChange={event => onInputChange(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                handleSend();
              }
            }}
            disabled={inputDisabled}
          />
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              ...(isSpLayout ? styles.primaryButtonSp : {}),
              opacity: canSend ? 1 : 0.5,
              cursor: canSend ? 'pointer' : 'not-allowed'
            }}
            onClick={handleSend}
            disabled={!canSend}
          >
            {sending ? '送信中...' : '送信'}
          </button>
        </div>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    borderRight: `1px solid ${theme.grayBorderColor}`,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden' as const
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#ffffff'
  },
  title: {
    fontSize: '16px',
    fontWeight: 600 as const,
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '12px',
    color: theme.grayTextColor
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center' as const,
    gap: '12px',
    minWidth: 0
  },
  hamburgerWrap: {
    display: 'flex',
    alignItems: 'center' as const,
    flexShrink: 0
  },
  infoButton: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    flexShrink: 0
  },
  infoBadge: {
    position: 'absolute' as const,
    top: '-2px',
    right: '-2px',
    minWidth: '16px',
    height: '16px',
    padding: '0 4px',
    borderRadius: '8px',
    backgroundColor: '#e0245e',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 700 as const,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    boxSizing: 'border-box' as const
  },
  headerButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center' as const,
    padding: '16px',
    gap: '12px',
    borderTop: `1px solid ${theme.grayBorderColor}`
  },
  inputAreaSp: {
    flexDirection: 'column' as const,
    alignItems: 'stretch' as const
  },
  textareaSp: {
    flex: 'none' as const,
    width: '100%' as const
  },
  primaryButtonSp: {
    width: '100%' as const,
    padding: '14px'
  },
  textarea: {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${theme.fieldBorderColor}`,
    resize: 'none' as const,
    fontFamily: 'inherit'
  },
  primaryButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '16px 48px',
    fontWeight: 600 as const,
    cursor: 'pointer'
  },
  secondaryButton: {
    backgroundColor: theme.contentBgColor,
    color: theme.grayTextColor,
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer'
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.grayTextColor
  }
});

export default ChatArea;
